import { useState } from "react";
import { Row } from "@tanstack/react-table";
import { Product } from "@/types/Products";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { MoreHorizontal } from "lucide-react";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import SingleEditProductDialog from "../SingleEditProductDialog";
import ViewOptimizationDialog from "../ViewOptimizationDialog";
import ApplyOptimalPriceDialog from "../ApplyOptimalPriceDialog";

export default function ProductActions({
    row,
    onRefresh,
    markets,
}: {
    row: Row<Product>;
    onRefresh: () => void;
    markets: string[];
}) {
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [isViewOpen, setIsViewOpen] = useState(false);
    const [isApplyPriceOpen, setIsApplyPriceOpen] = useState(false);
    const product = row.original;

    return (
        <>
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="h-8 w-8 p-0">
                        <span className="sr-only">Open menu</span>
                        <MoreHorizontal />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                    <DropdownMenuLabel>Optimization</DropdownMenuLabel>
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <div>
                                    <DropdownMenuItem
                                        onClick={() => setIsEditOpen(true)}
                                    >
                                        Settings
                                    </DropdownMenuItem>
                                </div>
                            </TooltipTrigger>
                            {product.status === "Optimizing" && (
                                <TooltipContent>
                                    <p>
                                        Cannot edit settings while optimization
                                        is in progress
                                    </p>
                                </TooltipContent>
                            )}
                        </Tooltip>

                        <Tooltip>
                            <TooltipTrigger asChild>
                                <div>
                                    <DropdownMenuItem
                                        onClick={() => setIsViewOpen(true)}
                                        disabled={
                                            product?.status !== "Optimizing" &&
                                            product?.status !== "Optimized"
                                        }
                                    >
                                        Analytics
                                    </DropdownMenuItem>
                                </div>
                            </TooltipTrigger>
                            {product?.status !== "Optimizing" && (
                                <TooltipContent>
                                    <p>
                                        Analytics are only available during
                                        optimization
                                    </p>
                                </TooltipContent>
                            )}
                        </Tooltip>

                        <Tooltip>
                            <TooltipTrigger asChild>
                                <div>
                                    <DropdownMenuItem
                                        onClick={() =>
                                            setIsApplyPriceOpen(true)
                                        }
                                        disabled={!product.is_converged}
                                    >
                                        Apply optimal price
                                    </DropdownMenuItem>
                                </div>
                            </TooltipTrigger>
                            {!product.is_converged && (
                                <TooltipContent>
                                    <p>
                                        {product.status === "Optimized"
                                            ? "Product has already been optimized"
                                            : "Product has not been optimized yet"}
                                    </p>
                                </TooltipContent>
                            )}
                        </Tooltip>
                    </TooltipProvider>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel>Information</DropdownMenuLabel>
                    <DropdownMenuItem
                        onClick={() => {
                            navigator.clipboard.writeText(product.sku);
                        }}
                    >
                        Copy SKU
                    </DropdownMenuItem>
                    <DropdownMenuItem
                        onClick={() => {
                            navigator.clipboard.writeText(
                                product.shopify_product_id
                            );
                        }}
                    >
                        Copy product ID
                    </DropdownMenuItem>
                    <DropdownMenuItem
                        onClick={() => {
                            navigator.clipboard.writeText(
                                product.shopify_variant_id
                            );
                        }}
                    >
                        Copy variant ID
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
            <SingleEditProductDialog
                product={product}
                markets={markets}
                isOpen={isEditOpen}
                onClose={() => {
                    setIsEditOpen(false);
                    onRefresh();
                }}
            />
            <ViewOptimizationDialog
                product={product}
                isOpen={isViewOpen}
                onClose={() => {
                    setIsViewOpen(false);
                }}
            />
            <ApplyOptimalPriceDialog
                product={product}
                isOpen={isApplyPriceOpen}
                onClose={() => {
                    setIsApplyPriceOpen(false);
                    onRefresh();
                }}
            />
        </>
    );
}
